import type { TypedSupabaseClient } from '../client';
import type {
  IProCategoryRepository,
  ProCategory,
  CreateProCategoryInput,
  UpdateProCategoryInput,
  ProCategoryFilters,
} from '../../../interfaces/src';

// Publishing layer over article_categories for ProSpace. Mirrors
// SupabaseStorefrontCategoryRepository (joins article_categories for the
// name/image fallback + parent_id so the catalog hierarchy can be resolved).
export class SupabaseProCategoryRepository implements IProCategoryRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: ProCategoryFilters): Promise<ProCategory[]> {
    let q = (this.supabase as any)
      .from('pro_categories')
      .select('*, article_categories(name, image_url, parent_id)');
    if (filters?.isPublished !== undefined) q = q.eq('is_published', filters.isPublished);
    const { data, error } = await q.order('display_order').order('created_at');
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapProCategory);
  }

  async listPublished(): Promise<ProCategory[]> {
    const { data, error } = await (this.supabase as any)
      .from('pro_categories')
      .select('*, article_categories(name, image_url, parent_id)')
      .eq('is_published', true)
      .order('display_order');
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapProCategory);
  }

  async getById(id: string): Promise<ProCategory | null> {
    const { data, error } = await (this.supabase as any)
      .from('pro_categories')
      .select('*, article_categories(name, image_url, parent_id)')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapProCategory(data as Record<string, unknown>) : null;
  }

  async create(input: CreateProCategoryInput): Promise<ProCategory> {
    const { data, error } = await (this.supabase as any)
      .from('pro_categories')
      .insert({
        article_category_id: input.articleCategoryId,
        name_fr: input.nameFr ?? null,
        name_ar: input.nameAr ?? null,
        name_en: input.nameEn ?? null,
        image_url: input.imageUrl ?? null,
        display_order: input.displayOrder ?? 0,
        is_published: input.isPublished ?? true,
      })
      .select('*, article_categories(name, image_url, parent_id)')
      .single();
    if (error) throw error;
    return mapProCategory(data as Record<string, unknown>);
  }

  async update(id: string, input: UpdateProCategoryInput): Promise<ProCategory> {
    const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (input.nameFr !== undefined) patch['name_fr'] = input.nameFr;
    if (input.nameAr !== undefined) patch['name_ar'] = input.nameAr;
    if (input.nameEn !== undefined) patch['name_en'] = input.nameEn;
    if (input.imageUrl !== undefined) patch['image_url'] = input.imageUrl;
    if (input.displayOrder !== undefined) patch['display_order'] = input.displayOrder;
    if (input.isPublished !== undefined) patch['is_published'] = input.isPublished;

    const { data, error } = await (this.supabase as any)
      .from('pro_categories')
      .update(patch)
      .eq('id', id)
      .select('*, article_categories(name, image_url, parent_id)')
      .single();
    if (error) throw error;
    return mapProCategory(data as Record<string, unknown>);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (this.supabase as any).from('pro_categories').delete().eq('id', id);
    if (error) throw error;
  }

  async reorder(ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await (this.supabase as any)
        .from('pro_categories')
        .update({ display_order: i })
        .eq('id', ids[i]);
      if (error) throw error;
    }
  }
}

function mapProCategory(row: Record<string, unknown>): ProCategory {
  const joined = row['article_categories'] as
    | { name?: string; image_url?: string | null; parent_id?: string | null }
    | null;
  return {
    id: row['id'] as string,
    articleCategoryId: row['article_category_id'] as string,
    parentArticleCategoryId: joined?.parent_id ?? null,
    nameFr: (row['name_fr'] as string) ?? null,
    nameAr: (row['name_ar'] as string) ?? null,
    nameEn: (row['name_en'] as string) ?? null,
    imageUrl: (row['image_url'] as string) ?? null,
    displayOrder: (row['display_order'] as number) ?? 0,
    isPublished: (row['is_published'] as boolean) ?? true,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    articleCategoryName: joined?.name,
    articleCategoryImageUrl: joined?.image_url ?? null,
  };
}
